import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export default function FAQSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                el.classList.remove('opacity-0');
              }, i * 80);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const questions = t('faq.questions', { returnObjects: true }) as Array<{ q: string; a: string }>;

  return (
    <section ref={sectionRef} id="faq" className="py-12 sm:py-16 lg:py-20 px-5 scroll-mt-16">
      <div className="max-w-lg sm:max-w-3xl lg:max-w-5xl mx-auto">
        <div className="reveal opacity-0 mb-6 lg:mb-10 lg:text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill rounded-full mb-3">
            <span className="w-1 h-1 rounded-full bg-[#00D4FF]" />
            <p className="text-[#00D4FF] text-[10px] font-semibold uppercase tracking-[0.12em]">{t('faq.label')}</p>
          </div>
          <h2 className="text-[#F0F2F5] text-2xl sm:text-3xl lg:text-4xl font-bold">{t('faq.title')}</h2>
        </div>

        <Accordion type="single" collapsible className="reveal opacity-0 grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-3">
          {questions.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}
              className="glass-card rounded-xl px-4 lg:px-5 border-0 overflow-hidden data-[state=open]:glass-card-active">
              <AccordionTrigger className="text-[#F0F2F5] text-[13px] lg:text-sm font-medium py-4 hover:no-underline hover:text-[#00D4FF] transition-colors text-left
                [&[data-state=open]]:text-[#00D4FF] [&>svg]:text-[#8A93A7] [&>svg]:w-4 [&>svg]:h-4">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#8A93A7] text-[12px] lg:text-[13px] leading-relaxed pb-4">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
